<template>
  <header
    class="ui-app-header"
    :class="[
      sticky && 'ui-app-header--sticky',
      bordered && 'ui-app-header--bordered'
    ]"
  >
    <div class="ui-app-header__inner">
      <component
        :is="brandComponent"
        v-bind="brandAttrs"
        class="ui-app-header__brand"
      >
        <span class="ui-app-header__logo">
          <slot name="logo">
            {{ brandLogo }}
          </slot>
        </span>
        <span class="ui-app-header__brand-text">
          <slot name="brand-title">
            <UiTruncatedText
              as="span"
              class="ui-app-header__title"
              :text="brandTitle"
              :lines="1"
            />
          </slot>
          <slot name="brand-version">
            <span
              v-if="brandVersion"
              class="ui-app-header__version"
            >
              {{ brandVersion }}
            </span>
          </slot>
        </span>
      </component>

      <div
        v-if="$slots.controls"
        class="ui-app-header__controls"
      >
        <slot name="controls" />
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import UiTruncatedText from './UiTruncatedText.vue';

type AppHeaderRouteTarget = string | Record<string, unknown>;

const props = withDefaults(
  defineProps<{
    brandTitle?: string;
    brandVersion?: string;
    brandLogo?: string;
    brandAriaLabel?: string;
    brandTo?: AppHeaderRouteTarget;
    brandHref?: string;
    sticky?: boolean;
    bordered?: boolean;
  }>(),
  {
    brandTitle: 'Krafta',
    brandVersion: '',
    brandLogo: 'K',
    brandAriaLabel: undefined,
    brandTo: undefined,
    brandHref: undefined,
    sticky: true,
    bordered: true
  }
);

const brandComponent = computed(() => {
  if (props.brandTo) return RouterLink;
  if (props.brandHref) return 'a';
  return 'div';
});

const brandAttrs = computed(() => {
  const ariaLabel = props.brandAriaLabel ?? props.brandTitle;

  if (props.brandTo) {
    return {
      to: props.brandTo,
      'aria-label': ariaLabel
    };
  }

  if (props.brandHref) {
    return {
      href: props.brandHref,
      'aria-label': ariaLabel
    };
  }

  return {
    'aria-label': ariaLabel
  };
});
</script>

<style scoped>
.ui-app-header {
  --ui-app-header-bg: color-mix(in srgb, var(--ui-surface-muted) 88%, transparent);
  --ui-app-header-text: var(--ui-foreground);
  --ui-app-header-muted: var(--ui-foreground-muted);
  --ui-app-header-height: 5rem;

  z-index: 30;
  min-height: var(--ui-app-header-height);
  background: var(--ui-app-header-bg);
  backdrop-filter: blur(18px) saturate(1.12);
  color: var(--ui-app-header-text);
  box-shadow: 0 1px 0 color-mix(in srgb, var(--ui-border) 70%, transparent);
}

.ui-app-header--sticky {
  position: sticky;
  top: 0;
}

.ui-app-header--bordered {
  border-bottom: 1px solid var(--ui-border);
}

.ui-app-header__inner {
  display: flex;
  width: 100%;
  min-height: var(--ui-app-header-height);
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0 2.125rem;
}

.ui-app-header__brand {
  display: inline-flex;
  min-width: 0;
  align-items: center;
  gap: 0.8125rem;
  border-radius: var(--ui-radius-lg);
  color: inherit;
  outline: none;
  text-decoration: none;
  transition:
    color 0.16s ease,
    transform 0.16s ease;
}

.ui-app-header__brand:hover {
  transform: translateY(-1px);
}

.ui-app-header__brand:focus-visible {
  box-shadow: var(--ui-focus-ring);
}

.ui-app-header__logo {
  display: inline-flex;
  width: 2.75rem;
  height: 2.75rem;
  flex: 0 0 2.75rem;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--ui-border);
  border-radius: 0.75rem;
  background: var(--ui-surface);
  color: var(--ui-accent);
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.5rem;
  box-shadow: var(--ui-control-shadow);
  transition:
    border-color 0.16s ease,
    box-shadow 0.16s ease,
    transform 0.16s ease;
}

.ui-app-header__brand:hover .ui-app-header__logo {
  border-color: color-mix(in srgb, var(--ui-accent) 32%, var(--ui-border));
  box-shadow:
    var(--ui-control-shadow),
    0 0.75rem 1.75rem color-mix(in srgb, var(--ui-accent) 12%, transparent);
}

.ui-app-header__brand-text {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 0.1875rem;
}

.ui-app-header__title {
  overflow: hidden;
  color: var(--ui-app-header-text);
  font-size: 1.125rem;
  font-weight: 700;
  line-height: 1.4375rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ui-app-header__version {
  color: var(--ui-app-header-muted);
  font-size: 0.875rem;
  line-height: 1.125rem;
}

.ui-app-header__controls {
  display: inline-flex;
  min-width: 0;
  flex: 0 0 auto;
  align-items: center;
  gap: 0.875rem;
}

@supports not ((-webkit-backdrop-filter: blur(8px)) or (backdrop-filter: blur(8px))) {
  .ui-app-header {
    --ui-app-header-bg: var(--ui-surface-muted);
  }
}

@media (max-width: 1024px) {
  .ui-app-header {
    --ui-app-header-height: 4.75rem;
  }

  .ui-app-header__inner {
    gap: 0.75rem;
    padding: 0 1rem;
  }

  .ui-app-header__controls {
    gap: 0.625rem;
  }
}

@media (max-width: 640px) {
  .ui-app-header__inner {
    padding: 0 1rem;
  }

  .ui-app-header__logo {
    width: 2.5rem;
    height: 2.5rem;
    flex-basis: 2.5rem;
    border-radius: 0.7rem;
    font-size: 1.125rem;
  }

  .ui-app-header__title {
    font-size: 1rem;
  }

  .ui-app-header__version {
    font-size: 0.75rem;
  }
}

@media (max-width: 640px) {
  .ui-app-header__inner {
    gap: 0.5rem;
    padding: 0 0.75rem;
  }

  .ui-app-header__brand {
    gap: 0.625rem;
  }

  .ui-app-header__version {
    display: none;
  }

  .ui-app-header__controls {
    flex: 0 1 auto;
    flex-wrap: wrap;
    justify-content: flex-end;
    max-width: 100%;
    gap: 0.5rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .ui-app-header__brand,
  .ui-app-header__logo {
    transition: none;
  }

  .ui-app-header__brand:hover {
    transform: none;
  }
}
</style>
